/**
 * SRS WebRTC 多线路竞速播放：同时连接所有线路，第一路出画面的绑定到 video，其余立即关闭。
 * 地址格式：webrtc://host/app/stream?expire=...&token=...（由 videoxx.php 签发）
 */

export interface RtcRaceOptions {
    onLoading: (loading: boolean) => void
    /** 全部线路失败、超时未出画面或播放中断时触发，调用方应重新获取地址再播 */
    onError: () => void
}

const START_TIMEOUT = 15000
const STALL_CHECK_INTERVAL = 10000

async function exchangeSdp(
    url: string,
    sdp: string,
    signal: AbortSignal
): Promise<string> {
    const host = new URL(url.replace(/^webrtc:/, 'https:')).host
    const api = `https://${host}/rtc/v1/play/`
    const res = await fetch(api, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api, streamurl: url, clientip: null, sdp }),
        signal
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    if (data.code !== 0) throw new Error(`SRS code ${data.code}`)
    return data.sdp
}

/**
 * @returns 停止播放并释放所有连接的函数
 */
export function playRtcRace(
    urls: string[],
    video: HTMLVideoElement,
    opts: RtcRaceOptions
): () => void {
    const abort = new AbortController()
    const pcs: RTCPeerConnection[] = []
    const lost = new Set<RTCPeerConnection>()
    let winner: RTCPeerConnection | null = null
    let disposed = false
    let startTimer = 0
    let stallTimer = 0
    let lastTime = -1

    const onWaiting = () => opts.onLoading(true)
    const onPlaying = () => opts.onLoading(false)

    const dispose = () => {
        if (disposed) return
        disposed = true
        abort.abort()
        clearTimeout(startTimer)
        clearInterval(stallTimer)
        pcs.forEach((pc) => pc.close())
        video.removeEventListener('waiting', onWaiting)
        video.removeEventListener('playing', onPlaying)
        video.srcObject = null
    }

    const fail = () => {
        if (disposed) return
        dispose()
        opts.onError()
    }

    const lose = (pc: RTCPeerConnection) => {
        if (disposed || winner || lost.has(pc)) return
        lost.add(pc)
        pc.close()
        if (lost.size === urls.length) fail()
    }

    const win = (pc: RTCPeerConnection, stream: MediaStream) => {
        if (disposed || winner || lost.has(pc)) return
        winner = pc
        clearTimeout(startTimer)
        pcs.forEach((p) => p !== pc && p.close())

        video.addEventListener('waiting', onWaiting)
        video.addEventListener('playing', onPlaying)
        video.muted = true
        video.srcObject = stream
        video.play().catch(() => undefined)
        opts.onLoading(false)

        pc.onconnectionstatechange = () => {
            if (pc.connectionState === 'failed') fail()
        }
        // 源端断流时 WebRTC 连接不会断开，只会画面停住，靠播放进度判断
        stallTimer = window.setInterval(() => {
            if (document.hidden) {
                lastTime = -1
                return
            }
            if (video.currentTime === lastTime) fail()
            lastTime = video.currentTime
        }, STALL_CHECK_INTERVAL)
    }

    const connect = async (url: string) => {
        const pc = new RTCPeerConnection()
        pcs.push(pc)
        pc.addTransceiver('audio', { direction: 'recvonly' })
        pc.addTransceiver('video', { direction: 'recvonly' })

        const stream = new MediaStream()
        pc.ontrack = (e) => {
            stream.addTrack(e.track)
            if (e.track.kind !== 'video') return
            // 远端轨道在收到第一个媒体包时 unmute，以此作为"出画面"
            if (!e.track.muted) win(pc, stream)
            else e.track.onunmute = () => win(pc, stream)
        }
        pc.oniceconnectionstatechange = () => {
            if (pc.iceConnectionState === 'failed') lose(pc)
        }

        try {
            const offer = await pc.createOffer()
            await pc.setLocalDescription(offer)
            const answer = await exchangeSdp(url, offer.sdp || '', abort.signal)
            if (disposed) return
            await pc.setRemoteDescription({ type: 'answer', sdp: answer })
        } catch {
            lose(pc)
        }
    }

    if (!urls.length) {
        setTimeout(fail, 0)
        return dispose
    }

    opts.onLoading(true)
    startTimer = window.setTimeout(fail, START_TIMEOUT)
    urls.forEach((url) => connect(url))

    return dispose
}
